import { useMemo } from 'react';

import CommentsPanel from '@/components/comments/CommentsPanel';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { templateSections } from '@/config/templateConfig';
import { SECTION_GROUP } from '@/constants';
import { DETAIL_MODE, useDetailMode } from '@/hooks/useDetailMode';
import AppendixView from '@/views/documents/AppendixView';
import ClauseView from '@/views/documents/ClauseView';
import DocumentApprovalsView from '@/views/documents/DocumentApprovalsView';
import HistoryView from '@/views/documents/HistoryView';
import InformationView from '@/views/documents/InformationView';
import ObjectiveScopeView from '@/views/documents/ObjectiveScopeView';
import ReferencesView from '@/views/documents/ReferencesView';
import ResponsibilitiesView from '@/views/documents/ResponsibilitiesView';
import TermsDefinitionsView from '@/views/documents/TermsDefinitionsView';

const SectionTabs = ({
  activeTab,
  detailType,
  setActiveTab,
  formData,
  setFormData,
  template,
  reviews,
  reviewerRole,
  docStatus,
  setReviews = () => {},
  setCanApprove,
  periodReviewValue,
  onPeriodReviewChange,
  mode: providedMode,
  revisions,
}) => {
  const { mode: routeMode } = useDetailMode(detailType);
  const mode = providedMode ?? routeMode;

  const contentItems = useMemo(() => templateSections['content'].items, []);

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <TabsList className="w-full flex mb-6 gap-x-2 gap-y-1 flex-wrap">
        <TabsTrigger value={SECTION_GROUP.INFORMATION.key}>
          {SECTION_GROUP.INFORMATION.label}
        </TabsTrigger>
        <TabsTrigger value={SECTION_GROUP.HISTORY.key}>
          {SECTION_GROUP.HISTORY.label}
        </TabsTrigger>
        {contentItems
          .filter(({ key }) => template.config[key])
          .map(({ key, label }) => (
            <TabsTrigger key={key} value={key}>
              {label}
            </TabsTrigger>
          ))}
        <TabsTrigger value="approvals">Approvals</TabsTrigger>
        {(mode === DETAIL_MODE.STATIC ||
          (mode === DETAIL_MODE.EDIT && docStatus === 'RETURNED')) &&
          docStatus !== 'DRAFT' &&
          reviews && <TabsTrigger value="summary">Review Summary</TabsTrigger>}
      </TabsList>

      <TabsContent value="information" className="mt-0">
        <InformationView
          document={formData}
          setDocument={setFormData}
          template={template}
        />
      </TabsContent>

      <TabsContent value="history" className="mt-0">
        <HistoryView template={template} revisions={revisions} />
      </TabsContent>

      <TabsContent value="objectiveAndScope" className="mt-0">
        <ObjectiveScopeView document={formData} setDocument={setFormData} />
      </TabsContent>

      <TabsContent value="terms" className="mt-0">
        <TermsDefinitionsView document={formData} setDocument={setFormData} />
      </TabsContent>

      <TabsContent value="references" className="mt-0">
        <ReferencesView document={formData} setDocument={setFormData} />
      </TabsContent>

      <TabsContent value="responsibilities" className="mt-0">
        <ResponsibilitiesView document={formData} setDocument={setFormData} />
      </TabsContent>

      <TabsContent value="clauseDetails" className="mt-0">
        <ClauseView document={formData} setDocument={setFormData} />
      </TabsContent>

      <TabsContent value="appendix" className="mt-0">
        <AppendixView document={formData} setDocument={setFormData} />
      </TabsContent>

      <TabsContent value="approvals" className="mt-0">
        <DocumentApprovalsView />
      </TabsContent>

      <TabsContent value="summary" className="mt-0">
        <CommentsPanel
          activeTab="summary"
          canSubmit={Boolean(reviewerRole && docStatus === 'REVIEWING')}
          reviews={reviews}
          reviewerRole={reviewerRole}
          periodReviewValue={periodReviewValue}
          setReviews={setReviews}
          setCanApprove={setCanApprove}
          onPeriodReviewChange={onPeriodReviewChange}
        />
      </TabsContent>
    </Tabs>
  );
};

export default SectionTabs;
